import React from 'react';
import { FileText, Shield, Database, CreditCard, AlertTriangle, Scale, Globe, Mail } from 'lucide-react';

const TermsAndConditionsPage = () => {
  const lastUpdated = "February 27, 2026";
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Terms and Conditions</h1>
          </div>
          <p className="text-slate-300">
            API Services & Platform Usage Agreement
          </p>
          <p className="text-sm text-slate-400 mt-2">
            Last Updated: {lastUpdated}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <p className="text-gray-700 leading-relaxed">
            PLEASE READ THESE TERMS CAREFULLY BEFORE USING THE MYEUROPACIFIC PLATFORM AND/OR API SERVICES. 
            BY REGISTERING FOR OR USING OUR SERVICES, YOU AGREE TO BE BOUND BY THESE TERMS ON BEHALF OF 
            THE BUSINESS YOU REPRESENT. IF YOU DO NOT AGREE WITH THESE TERMS, DO NOT ACCESS OR USE OUR SERVICES.
          </p>
        </div>

        {/* Section 1: Definitions */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</span>
            Definitions
          </h2>
          <div className="space-y-3 text-gray-700">
            <p><strong>"Agreement"</strong> means these Terms and Conditions, including any referenced addendums, amendments, and order forms.</p>
            <p><strong>"API"</strong> means the Application Programming Interface provided by myEuropacific for integration with third-party systems.</p>
            <p><strong>"Customer" or "You"</strong> means the business entity accessing or using the Platform or API Services.</p>
            <p><strong>"Platform"</strong> means the myEuropacific proprietary logistics management system, including all associated services and features.</p>
            <p><strong>"Platform User"</strong> means a registered user of the myEuropacific Platform with an active subscription.</p>
            <p><strong>"External User"</strong> means any third party accessing myEuropacific API Services without being a registered Platform User.</p>
            <p><strong>"Services"</strong> means all products, data, and information provided through the Platform and API.</p>
            <p><strong>"Transaction Data"</strong> means any data transmitted to, from, or through the Platform or API by or about the Customer.</p>
            <p><strong>"Vendor," "We," "Our," or "Us"</strong> means EUROPACIFIC LOGISTIKA D.O.O. and its affiliates.</p>
          </div>
        </section>

        {/* Section 2: API Access & Pricing */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
            <CreditCard className="w-5 h-5 text-blue-600" />
            API Access & Pricing
          </h2>
          
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-green-800 mb-2">2.1 Platform Users - FREE API Access</h3>
            <p className="text-green-700">
              Registered Platform Users with an active subscription receive <strong>complimentary API access</strong> as part of their platform membership. 
              This includes full access to all API endpoints for integration with your internal systems and approved third-party applications.
            </p>
          </div>

          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-amber-800 mb-2">2.2 External Users - API Subscription</h3>
            <p className="text-amber-700 mb-3">
              Third parties wishing to connect to myEuropacific API Services without a Platform subscription are subject to the following fees:
            </p>
            <div className="bg-white rounded-lg p-4 border border-amber-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">€300</p>
                  <p className="text-sm text-gray-600">per month</p>
                </div>
                <div className="text-right text-sm text-gray-600">
                  <p>Billed monthly in advance</p>
                  <p>Minimum 12-month commitment</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3 text-gray-700">
            <p><strong>2.3</strong> All prices are in Euros (EUR) and exclude applicable VAT/taxes.</p>
            <p><strong>2.4</strong> Fees are non-refundable except as expressly stated in this Agreement.</p>
            <p><strong>2.5</strong> We reserve the right to modify pricing with 90 days' prior written notice.</p>
            <p><strong>2.6</strong> Annual price increases of up to 5% may be applied without notice.</p>
            <p><strong>2.7</strong> Late payments accrue interest at 1.5% per month or the maximum rate permitted by law.</p>
          </div>
        </section>

        {/* Section 3: Use of Services */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</span>
            <Shield className="w-5 h-5 text-blue-600" />
            Use of Services
          </h2>
          <div className="space-y-3 text-gray-700">
            <p><strong>3.1</strong> We grant You a non-exclusive, non-transferable, worldwide right during the term of this Agreement to access and use the Services and API solely for Your internal business purposes.</p>
            <p><strong>3.2</strong> Services are designed exclusively for bona fide business use. Individual consumer use is prohibited.</p>
            <p><strong>3.3</strong> Any use for purposes competitive with myEuropacific or that may devalue our commercial interests is expressly prohibited.</p>
            <p><strong>3.4</strong> You are responsible for:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Maintaining the security of API credentials and access tokens</li>
              <li>Ensuring accuracy and completeness of Transaction Data</li>
              <li>Compliance with all applicable laws and regulations</li>
              <li>Actions of users authorized under Your account</li>
            </ul>
            <p><strong>3.5</strong> We may modify, update, or discontinue any Service with 30 days' prior notice.</p>
            <p><strong>3.6</strong> We reserve the right to suspend access for security threats or policy violations without prior notice.</p>
          </div>
        </section>

        {/* Section 4: API Rate Limits */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</span>
            <Database className="w-5 h-5 text-blue-600" />
            API Usage & Rate Limits
          </h2>
          <div className="space-y-3 text-gray-700">
            <p><strong>4.1</strong> API access is subject to rate limits to ensure fair usage and system stability:</p>
            <div className="bg-gray-50 rounded-lg p-4 my-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Tier</th>
                    <th className="text-left py-2">Requests/Minute</th>
                    <th className="text-left py-2">Requests/Day</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2">Platform User</td>
                    <td className="py-2">100</td>
                    <td className="py-2">50,000</td>
                  </tr>
                  <tr>
                    <td className="py-2">External API</td>
                    <td className="py-2">60</td>
                    <td className="py-2">25,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p><strong>4.2</strong> Higher rate limits may be negotiated for enterprise customers.</p>
            <p><strong>4.3</strong> Exceeding rate limits may result in temporary access suspension or additional charges.</p>
            <p><strong>4.4</strong> We monitor API usage and reserve the right to throttle excessive requests.</p>
          </div>
        </section>

        {/* Section 5: Intellectual Property */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">5</span>
            Intellectual Property Rights
          </h2>
          <div className="space-y-3 text-gray-700">
            <p><strong>5.1</strong> We retain all intellectual property rights in the Platform, Services, API, and all associated materials.</p>
            <p><strong>5.2</strong> You retain ownership of Your Transaction Data. You grant us a worldwide, royalty-free license to use such data for providing the Services and improving our Platform.</p>
            <p><strong>5.3</strong> We may create anonymized, aggregated data from Transaction Data for analytical purposes. Such derived data is owned by us.</p>
            <p><strong>5.4</strong> You may not:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Reverse engineer, decompile, or disassemble the API or Platform</li>
              <li>Create derivative works based on our Services</li>
              <li>Redistribute, sublicense, or resell API access without authorization</li>
              <li>Use our trademarks without prior written consent</li>
            </ul>
          </div>
        </section>

        {/* Section 6: Data Protection */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">6</span>
            <Globe className="w-5 h-5 text-blue-600" />
            Data Protection & Privacy
          </h2>
          <div className="space-y-3 text-gray-700">
            <p><strong>6.1</strong> Both parties shall comply with applicable data protection laws, including GDPR where applicable.</p>
            <p><strong>6.2</strong> For Platform and API Services, We act as a data processor; You are the data controller.</p>
            <p><strong>6.3</strong> Personal data may be transferred to countries outside the EEA. We implement appropriate safeguards including Standard Contractual Clauses.</p>
            <p><strong>6.4</strong> You warrant that You have obtained all necessary consents for processing personal data through our Services.</p>
            <p><strong>6.5</strong> We implement industry-standard security measures including:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>TLS 1.2+ encryption for data in transit</li>
              <li>AES-256 encryption for data at rest</li>
              <li>Regular security audits and penetration testing</li>
              <li>SOC 2 compliance standards</li>
            </ul>
          </div>
        </section>

        {/* Section 7: Confidentiality */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">7</span>
            Confidentiality
          </h2>
          <div className="space-y-3 text-gray-700">
            <p><strong>7.1</strong> Each party agrees to hold the other's Confidential Information in confidence for the duration of this Agreement and 3 years thereafter.</p>
            <p><strong>7.2</strong> Confidential Information excludes information that:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Is publicly available through no fault of the receiving party</li>
              <li>Was already in the receiving party's possession</li>
              <li>Is independently developed by the receiving party</li>
              <li>Must be disclosed by law or court order</li>
            </ul>
          </div>
        </section>

        {/* Section 8: Warranties & Disclaimer */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">8</span>
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Warranties & Disclaimer
          </h2>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
            <p className="font-semibold mb-2">DISCLAIMER OF WARRANTIES</p>
            <p className="text-sm">
              THE PLATFORM, SERVICES, AND API ARE PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. 
              WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE. 
              WE DISCLAIM ALL WARRANTIES INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </div>
          <div className="mt-4 space-y-3 text-gray-700">
            <p><strong>8.1</strong> We strive to maintain 99.5% uptime but do not guarantee uninterrupted service.</p>
            <p><strong>8.2</strong> Third-party data and carrier information are provided without guarantee of accuracy or timeliness.</p>
            <p><strong>8.3</strong> We are not responsible for data accuracy submitted by You or third parties.</p>
          </div>
        </section>

        {/* Section 9: Limitation of Liability */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">9</span>
            <Scale className="w-5 h-5 text-blue-600" />
            Limitation of Liability
          </h2>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 mb-4">
            <p className="font-semibold mb-2">LIMITATION OF LIABILITY</p>
            <p className="text-sm">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
              CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, 
              EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
          </div>
          <div className="space-y-3 text-gray-700">
            <p><strong>9.1</strong> Our maximum aggregate liability shall not exceed the fees paid by You in the 12 months preceding the claim, or €5,000, whichever is greater.</p>
            <p><strong>9.2</strong> Claims must be brought within one year of the event giving rise to the claim.</p>
            <p><strong>9.3</strong> These limitations reflect the allocation of risk between the parties and shall survive termination.</p>
          </div>
        </section>

        {/* Section 10: Indemnification */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">10</span>
            Indemnification
          </h2>
          <div className="space-y-3 text-gray-700">
            <p><strong>10.1</strong> You agree to indemnify, defend, and hold harmless myEuropacific and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Your breach of this Agreement</li>
              <li>Your use of or access to the Services</li>
              <li>Your violation of any third-party rights</li>
              <li>Your Transaction Data</li>
            </ul>
          </div>
        </section>

        {/* Section 11: Term & Termination */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">11</span>
            Term & Termination
          </h2>
          <div className="space-y-3 text-gray-700">
            <p><strong>11.1</strong> This Agreement continues until terminated in accordance with these terms.</p>
            <p><strong>11.2</strong> Either party may terminate:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>With 30 days' written notice</li>
              <li>Immediately if the other party materially breaches and fails to cure within 30 days</li>
              <li>Immediately upon insolvency or bankruptcy of the other party</li>
            </ul>
            <p><strong>11.3</strong> Upon termination:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>All API credentials and access rights are immediately revoked</li>
              <li>Outstanding fees become immediately due</li>
              <li>You must cease use of all Services and destroy confidential materials</li>
            </ul>
            <p><strong>11.4</strong> Sections relating to intellectual property, confidentiality, limitation of liability, and indemnification survive termination.</p>
          </div>
        </section>

        {/* Section 12: Governing Law */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">12</span>
            Governing Law & Dispute Resolution
          </h2>
          <div className="space-y-3 text-gray-700">
            <p><strong>12.1</strong> This Agreement shall be governed by and construed in accordance with the laws of the Republic of Slovenia.</p>
            <p><strong>12.2</strong> Any disputes shall be submitted to the exclusive jurisdiction of the courts of Ljubljana, Slovenia.</p>
            <p><strong>12.3</strong> The parties agree to attempt good-faith negotiation before initiating legal proceedings.</p>
            <p><strong>12.4</strong> The United Nations Convention on Contracts for the International Sale of Goods is expressly excluded.</p>
          </div>
        </section>

        {/* Section 13: General */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">13</span>
            General Provisions
          </h2>
          <div className="space-y-3 text-gray-700">
            <p><strong>13.1</strong> This Agreement constitutes the entire agreement between the parties.</p>
            <p><strong>13.2</strong> No amendment shall be valid unless in writing and signed by both parties.</p>
            <p><strong>13.3</strong> You may not assign this Agreement without our prior written consent.</p>
            <p><strong>13.4</strong> Failure to enforce any provision shall not constitute a waiver.</p>
            <p><strong>13.5</strong> If any provision is found unenforceable, the remaining provisions shall continue in effect.</p>
            <p><strong>13.6</strong> We may update these Terms at any time. Continued use constitutes acceptance of changes.</p>
          </div>
        </section>

        {/* Contact Information */}
        <section className="bg-slate-800 text-white rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Contact Information
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="font-semibold">EUROPACIFIC LOGISTIKA D.O.O.</p>
              <p className="text-slate-300 text-sm mt-2">
                Cesta v Mestni log 55<br />
                1000 Ljubljana<br />
                Slovenia
              </p>
            </div>
            <div className="text-sm text-slate-300">
              <p><strong className="text-white">General Inquiries:</strong> info@europacific.com</p>
              <p><strong className="text-white">API Support:</strong> api@europacific.com</p>
              <p><strong className="text-white">Legal:</strong> legal@europacific.com</p>
              <p className="mt-2"><strong className="text-white">VAT:</strong> SI12345678</p>
            </div>
          </div>
        </section>

        {/* Acceptance */}
        <div className="mt-6 text-center text-gray-500 text-sm">
          <p>By using myEuropacific Platform or API Services, you acknowledge that you have read,</p>
          <p>understood, and agree to be bound by these Terms and Conditions.</p>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
